import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import Honeybadger from '@honeybadger-io/js';
import { useQueryClient } from '@tanstack/react-query';

import { requestApi } from 'api/Api';

import useModal from 'lib/hooks/useModal';

import { showAlert } from 'components/shared/Alert/Alert';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';

import constant from '../lib/constant';

import TerminalModal from './TerminalModal';

export const Actions = ({ data }) => {
  const { isOpen, openModal, closeModal } = useModal();
  const [modalType, setModalType] = useState(null);
  const [deletionModal, setDeletionModal] = useState(false);
  const queryClient = useQueryClient();

  const triggerEditModal = (type) => {
    setModalType(type);
    openModal();
  };

  const navigate = useNavigate();

  const updateTerminal = async (newStatus = constant.deleted) => {
    try {
      const res = await requestApi({
        url: '/api/transactions/terminal/update',
        params: {
          changes: (() => {
            const { checks: _checks, terminalResponse: _terminalResponse, ...rest } = data;
            return { ...rest, status: newStatus };
          })()
        },
        navigate
      });

      const { code, redirect, error } = res;
      switch (code) {
        case -1:
          navigate(redirect);
          break;
        case 0:
          showAlert({ title: `Deleted terminal ${data.id}`, color: 'success' });
          await queryClient.invalidateQueries(['terminal_admin']);
          break;
        default:
          Honeybadger.notify(
            `file: /terminals/terminalEntry, method: saveChanges - try, error: ${
              error ?? 'An unexpected error has occurred.'
            }`
          );
          if (error) toast.error(error);
          break;
      }
    } catch (error) {
      Honeybadger.notify(
        `file: /terminals/terminalEntry, method: saveChanges - catch, error: ${
          error ?? 'An unexpected error has occurred.'
        }`
      );
    }
  };

  const changeTerminalActivity = async () => {
    try {
      const res = await requestApi({
        url: '/api/transactions/terminal/update',
        params: {
          changes: (() => {
            const { checks: _checks, terminalResponse: _terminalResponse, ...rest } = data;
            return {
              ...rest,
              active: !data?.active,
              status: !data?.active ? 'active' : 'inactive'
            };
          })()
        },
        navigate
      });

      const { code, redirect, error } = res;
      switch (code) {
        case -1:
          navigate(redirect);
          break;
        case 0:
          showAlert({
            title: data?.active
              ? `Terminal ${data?.id} Deactivated`
              : `Terminal ${data?.id} Activated`,
            color: 'success'
          });
          await queryClient.invalidateQueries(['terminal_admin']);
          break;
        default:
          Honeybadger.notify(
            `file: /terminals/terminalEntry, method: saveChanges - try, error: ${
              error ?? 'An unexpected error has occurred.'
            }`
          );
          if (error) toast.error(error);
          break;
      }
    } catch (error) {
      Honeybadger.notify(
        `file: /terminals/terminalEntry, method: saveChanges - catch, error: ${
          error ?? 'An unexpected error has occurred.'
        }`
      );
    }
  };

  const actionOptions = [
    { label: 'Edit', icon: 'edit-2-mono', onClick: () => triggerEditModal('edit') },
    { label: 'Delete', icon: 'trash-mono', onClick: () => setDeletionModal(true) },
    {
      label: data?.active ? 'Deactivate' : 'Activate',
      icon: 'close-circle-mono',
      onClick: () => changeTerminalActivity()
    },
    ...(data?.status !== constant?.temporaryDeactivation
      ? [
          {
            label: 'Temporarily Deactivate',
            icon: 'close-circle-mono',
            onClick: () => updateTerminal(constant.temporaryDeactivation)
          }
        ]
      : [])
  ];

  return (
    <>
      <Popover
        isFixed
        position="left"
        isBorder={false}
        options={actionOptions}
        className="flex h-full items-center"
      />
      <Confirm
        isOpen={deletionModal}
        title={`Delete Terminal ${data?.id}`}
        message="Are you sure you want to delete this terminal?"
        variant="danger"
        icon="trash-mono"
        handleClose={() => setDeletionModal(false)}
        handleContinue={() => {
          updateTerminal(constant.deleted);
          setDeletionModal(false);
        }}
      />
      <TerminalModal item={data} isOpen={isOpen} type={modalType} handleClose={closeModal} />
    </>
  );
};

export default Actions;
